import { ReactNode } from 'react';
import {
  Box,
  Stack,
  Typography,
  TypographyTypeMap,
  StackProps,
  SxProps,
  Theme,
} from '@mui/material';
import CopyableText from './CopyableText';
import tokens from '@verifime/design-tokens';

type TypographyVariant = TypographyTypeMap['props']['variant'];

export type TNameValueDisplayType = 'vertical' | 'horizontal';

export type TNameValueDisplayProps = {
  name: string;
  value?: ReactNode;
  copyable?: boolean;
  displayType?: TNameValueDisplayType;
  nameColor?: TypographyTypeMap['props']['color'];
  valueColor?: TypographyTypeMap['props']['color'];
  nameVariant?: TypographyVariant;
  valueVariant?: TypographyVariant;
  /**
   * Styling for the value only, never the label. `sx` on this component's own container props
   * reaches both the name and the value `Typography` (`text-transform` and friends inherit down
   * the `Stack`), so a caller that wants to change how just the value paints, e.g. the "Name on
   * document" column upper-casing its value while leaving the label beside it alone, needs a
   * separate hook that never touches the label.
   *
   * Has no effect on either of the other two branches, for different reasons. When `copyable` is
   * true the value renders through `CopyableText`, which declares a closed set of props with no
   * `sx`, `style` or `className` among them, so there is nothing for this to reach. When `value`
   * is not a string/number/boolean it renders through a `Box`, which does accept `sx`: that
   * branch is simply not wired to receive `valueSx` today, so extending it is a one-line change
   * rather than a limitation to work around.
   */
  valueSx?: SxProps<Theme>;
} & StackProps;

function HorizontalNameValueDisplay({
  nameVariant = 'body2',
  alignItems = 'center',
  gap = tokens.spacing2xs,
  ...props
}: Omit<TNameValueDisplayProps, 'displayType' | 'direction'>) {
  return (
    <NameValueDisplayInternal
      nameVariant={nameVariant}
      flex={1}
      gap={gap}
      alignItems={alignItems}
      {...props}
      direction="row"
    />
  );
}

function VerticalNameValueDisplay({
  nameVariant = 'caption',
  alignItems = 'flex-start',
  gap = tokens.spacing3xs,
  ...props
}: Omit<TNameValueDisplayProps, 'displayType' | 'direction'>) {
  return (
    <NameValueDisplayInternal
      nameVariant={nameVariant}
      flex={1}
      gap={gap}
      alignItems={alignItems}
      {...props}
      direction="column"
    />
  );
}

function NameValueDisplayInternal({
  name,
  value,
  copyable = false,
  nameVariant,
  nameColor = 'textSecondary',
  valueColor = 'textPrimary',
  valueVariant = 'body2',
  valueSx,
  ...containerProps
}: Omit<TNameValueDisplayProps, 'nameVariant'> & { nameVariant: TypographyVariant }) {
  return (
    <Stack {...containerProps}>
      <Typography color={nameColor} variant={nameVariant}>
        {name}
      </Typography>
      {['string', 'number', 'boolean'].includes(typeof value) ? (
        copyable ? (
          <CopyableText
            text={value}
            notAvailableText="n/a"
            textColor={valueColor}
            textVariant={valueVariant}
          />
        ) : (
          <Typography
            color={valueColor}
            variant={valueVariant}
            // Array-form sx, not object-spread: `valueSx` can itself be a function or an array
            // (that is what `SxProps<Theme>` allows), and spreading a function into an object
            // silently drops it, so a caller passing `valueSx={(theme) => ({...})}` would
            // typecheck but render nothing. MUI merges an sx array left-to-right, so this
            // composes with the base style regardless of which form the caller passed.
            sx={[
              { whiteSpace: 'pre-wrap' },
              ...(Array.isArray(valueSx) ? valueSx : valueSx ? [valueSx] : []),
            ]}
          >
            {value}
          </Typography>
        )
      ) : (
        <Box minHeight={tokens.fontSizeBase}>{value}</Box>
      )}
    </Stack>
  );
}

export default function NameValueDisplay({
  displayType = 'vertical',
  ...props
}: TNameValueDisplayProps) {
  return displayType === 'vertical' ? (
    <VerticalNameValueDisplay {...props} />
  ) : (
    <HorizontalNameValueDisplay {...props} />
  );
}
